import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Stack, Typography, TextField, Autocomplete, Select, MenuItem,
  FormControl, IconButton, Tooltip, Divider, InputLabel,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useData } from '../context/DataContext';
import type { Bill } from '../data/types';
import { BRAND, BRAND_LIGHT, BRAND_BORDER, INK, INK_SOFT } from '../theme/tokens';

const INVOICE_TERMS = ['7 Day Terms', '14 Day Terms', '30 Days EOM', '30 Days End of Month', '60 Days EOM', 'COD', 'Payment on Completion'];
const DIVISIONS = ['Service Div', 'Field Div', 'Projects Div', 'Admin'];

const labelSx = {
  fontSize: '0.62rem', fontWeight: 700, color: INK_SOFT,
  textTransform: 'uppercase' as const, letterSpacing: 0.4, mb: 0.4, display: 'block',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: BRAND_LIGHT, borderRadius: 1, fontSize: '0.82rem',
    '& fieldset': { borderColor: 'transparent' },
    '&:hover fieldset': { borderColor: BRAND_BORDER },
    '&.Mui-focused fieldset': { borderColor: BRAND, borderWidth: 1 },
  },
  '& .MuiInputBase-input': { py: 0.7, px: 1, color: INK, fontWeight: 500 },
  '& .MuiSelect-select': { py: 0.7, px: 1 },
};

const factSx = { fontSize: '0.72rem', color: INK_SOFT, lineHeight: 1.5 };

interface BillEditFormProps {
  bill: Bill;
}

export const BillEditForm: React.FC<BillEditFormProps> = ({ bill }) => {
  const { suppliers, projects, updateBill } = useData();
  const [draft, setDraft] = useState<Bill>(bill);
  useEffect(() => { setDraft(bill); }, [bill.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const commitTimer = useRef<number | null>(null);
  useEffect(() => {
    if (commitTimer.current) window.clearTimeout(commitTimer.current);
    const drift = Object.keys(draft).some(k => (draft as any)[k] !== (bill as any)[k]);
    if (!drift) return;
    commitTimer.current = window.setTimeout(() => {
      const updates: Partial<Bill> = {};
      Object.keys(draft).forEach(k => {
        if ((draft as any)[k] !== (bill as any)[k]) (updates as any)[k] = (draft as any)[k];
      });
      if (Object.keys(updates).length > 0) updateBill(bill.id, updates);
    }, 400);
    return () => { if (commitTimer.current) window.clearTimeout(commitTimer.current); };
  }, [draft, bill, updateBill]);

  const set = <K extends keyof Bill>(key: K, value: Bill[K]) => {
    setDraft(prev => ({ ...prev, [key]: value }));
  };

  const supplier = suppliers.find(s => s.id === draft.supplierId);
  const project  = projects.find(p => p.id === draft.projectId);

  const setDueDays = (days: number) => {
    set('dueDays', days);
    if (draft.date) {
      const d = new Date(draft.date);
      d.setDate(d.getDate() + days);
      set('dueDate', d.toISOString().slice(0, 10));
    }
  };

  return (
    <Box sx={{ p: 2.5, backgroundColor: '#fff', border: '1px solid #e0e0e0', borderRadius: 1.5 }}>
      <Section title="Supplier">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Autocomplete size="small" fullWidth options={suppliers}
            getOptionLabel={s => s.name} value={supplier ?? null}
            onChange={(_, v) => set('supplierId', v?.id ?? '')}
            renderInput={params => <TextField {...params} placeholder="Search supplier" sx={inputSx} />}
          />
          <AddInlineButton tooltip="Create new supplier" />
        </Stack>
        <FactGrid>
          <Fact label="Business name" value={supplier?.name} />
          <Fact label="Business number" value={supplier?.abn ? `ABN ${supplier.abn}` : undefined} />
          <Fact label="Address" value={supplier?.address} />
          <Fact label="Email" value={supplier?.email} />
        </FactGrid>
      </Section>

      <Divider sx={{ my: 2.5 }} />

      <Section title="Project">
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Autocomplete size="small" fullWidth options={projects}
            getOptionLabel={p => p.name} value={project ?? null}
            onChange={(_, v) => set('projectId', v?.id)}
            renderInput={params => <TextField {...params} placeholder="Search project" sx={inputSx} />}
          />
          <Tooltip title="Open project in new tab" arrow>
            <span>
              <IconButton size="small" disabled={!project}
                sx={{ color: BRAND, '&:hover': { backgroundColor: BRAND_LIGHT }, '&.Mui-disabled': { color: '#ccc' } }}>
                <OpenInNewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
          <AddInlineButton tooltip="Create new project" />
        </Stack>
        <FactGrid>
          <Fact label="Project name" value={project?.name} />
          <Fact label="Project number" value={project?.number} />
          <Fact label="Start date" value={project?.startDate} />
          <Fact label="End date" value={project?.endDate} />
        </FactGrid>
      </Section>

      <Divider sx={{ my: 2.5 }} />

      <Section title="Details">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 1.5 }}>
          <Field label="Invoice term">
            <SelectField value={draft.invoiceTerm ?? ''} onChange={v => set('invoiceTerm', v)} options={INVOICE_TERMS} allowEmpty />
          </Field>
          <Field label="Date" required>
            <DateField value={draft.date} onChange={v => set('date', v ?? '')} />
          </Field>
          <Field label="Due days">
            <TextField size="small" type="number" fullWidth sx={inputSx}
              value={draft.dueDays ?? ''} onChange={e => setDueDays(parseInt(e.target.value, 10) || 0)} />
          </Field>
          <Field label="Due date">
            <DateField value={draft.dueDate} onChange={v => set('dueDate', v)} />
          </Field>
          <Field label="Supplier invoice #">
            <TextField size="small" fullWidth sx={inputSx} value={draft.supplierInvoiceNo ?? ''} onChange={e => set('supplierInvoiceNo', e.target.value)} />
          </Field>
          <Field label="Purchase order #">
            <TextField size="small" fullWidth sx={inputSx} value={draft.linkedPoId ?? ''} onChange={e => set('linkedPoId', e.target.value || null)} />
          </Field>
          <Field label="Reference">
            <TextField size="small" fullWidth sx={inputSx} value={draft.reference ?? ''} onChange={e => set('reference', e.target.value)} />
          </Field>
          <Field label="Owner / Sales Rep">
            <TextField size="small" fullWidth sx={inputSx} value={draft.ownerSalesRep ?? ''} onChange={e => set('ownerSalesRep', e.target.value)} />
          </Field>
          <Field label="Division">
            <SelectField value={draft.division ?? ''} onChange={v => set('division', v)} options={DIVISIONS} allowEmpty />
          </Field>
          <Field label="Business entity">
            <TextField size="small" fullWidth sx={inputSx} value={draft.businessEntity ?? ''} onChange={e => set('businessEntity', e.target.value)} />
          </Field>
        </Box>
        <Field label="Internal notes">
          <TextField size="small" fullWidth multiline minRows={2} sx={{ ...inputSx, mt: 1 }}
            value={draft.notes ?? ''} onChange={e => set('notes', e.target.value)} />
        </Field>
      </Section>

      <Typography sx={{ fontSize: '0.65rem', color: INK_SOFT, mt: 2, fontStyle: 'italic' }}>
        Changes save automatically.
      </Typography>
    </Box>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: INK, mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);

const Field: React.FC<{ label: string; required?: boolean; children: React.ReactNode }> = ({ label, required, children }) => (
  <Box>
    <InputLabel shrink sx={{ ...labelSx, position: 'static', transform: 'none' }}>
      {label}{required && <span style={{ color: BRAND, marginLeft: 2 }}>*</span>}
    </InputLabel>
    {children}
  </Box>
);

const Fact: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', gap: 0.6, alignItems: 'baseline' }}>
    <Typography sx={{ fontSize: '0.7rem', color: INK_SOFT, fontWeight: 600, minWidth: 110 }}>{label}:</Typography>
    <Typography sx={factSx}>{value && value.trim() ? value : '—'}</Typography>
  </Box>
);

const FactGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 3, rowGap: 0.4, pl: 0.5 }}>{children}</Box>
);

const SelectField: React.FC<{
  value: string; onChange: (v: string) => void; options: readonly string[]; allowEmpty?: boolean;
}> = ({ value, onChange, options, allowEmpty }) => (
  <FormControl size="small" fullWidth>
    <Select value={value} displayEmpty sx={inputSx} onChange={e => onChange(e.target.value)}>
      {allowEmpty && <MenuItem value=""><em>add</em></MenuItem>}
      {options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
    </Select>
  </FormControl>
);

const DateField: React.FC<{ value?: string; onChange: (v: string) => void }> = ({ value, onChange }) => (
  <TextField size="small" type="date" fullWidth sx={inputSx}
    value={value ?? ''} onChange={e => onChange(e.target.value)} InputLabelProps={{ shrink: true }} />
);

const AddInlineButton: React.FC<{ tooltip: string }> = ({ tooltip }) => (
  <Tooltip title={tooltip} arrow>
    <IconButton size="small"
      sx={{ backgroundColor: BRAND, color: '#fff', borderRadius: 1, p: 0.4, '&:hover': { backgroundColor: '#4a0a82' } }}>
      <AddIcon sx={{ fontSize: 18 }} />
    </IconButton>
  </Tooltip>
);

export default BillEditForm;
